// An authenticated BLE session with one device.
//
// Everything that talks to a device goes through here, because the ORDER is not
// negotiable and getting it wrong fails silently at every step:
//
//   connect                  (bleClient also negotiates the MTU here)
//   enable notifications     BEFORE any write — replies are notifications, and
//                            the device answers in milliseconds
//   AUTH handshake           commands are rejected until this completes
//   … commands …
//
// Two stages of auth, per §3:
//   stage 1  proves "this is the Smartify app"        — every device
//   stage 2  proves "this is the owner"               — assigned devices only
//
// ⚠️ THREE FAILED ATTEMPTS AND THE DEVICE DROPS THE LINK. So a failure is
// reported, never retried blindly — a retry loop against a wrong key locks the
// device out and looks like broken hardware.

import {
  connect, disconnect, listen, write, bleSupported,
} from './bleClient';
import {
  CMD_AUTH, buildAuthAnswer, buildOwnerAnswer,
} from './bleConfig';
import {
  authResponse, ownerResponse, parseNonce, isAuthOk, isAuthFail, isOwnerRequired,
  DEFAULT_KEY_VERSION,
} from './bleAuth';

// The handshake is three round-trips over a local radio link. Generous enough
// for a busy phone, short enough that a dead device does not hold a screen.
export const HANDSHAKE_TIMEOUT_MS = 10000;

/**
 * Open an authenticated session.
 *
 * @param deviceId      the platform's BLE handle (NOT the unique number)
 * @param uniqueNumber  hex MAC, e.g. "083A8D406BE4" — what the key derives from
 * @param opts.ownerSecret  required only for an ASSIGNED device; hex or bytes
 * @param opts.onStatus     called with every notification that is not part of
 *                          the handshake — including unsolicited ones, which is
 *                          how a physical press reaches the UI
 * @param opts.onDisconnect called when the link drops for any reason
 * @param opts.onTrace      every byte in and out, as text — for hardware
 *                          bring-up, where "auth failed" with no visible
 *                          exchange is not something anyone can act on
 *
 * -> { ok: true, session } | { ok: false, error, code }
 */
export async function openSession(deviceId, uniqueNumber, opts = {}) {
  if (!bleSupported()) {
    return { ok: false, code: 'UNSUPPORTED', error: 'Bluetooth is only available in the app.' };
  }

  const {
    ownerSecret = null,
    onStatus = null,
    onDisconnect = null,
    onTrace = null,
    keyVersion = DEFAULT_KEY_VERSION,
  } = opts;

  const trace = (dir, text) => {
    if (onTrace) {
      try { onTrace(dir, text); } catch (e) { /* a tracer must never break the link */ }
    }
  };

  let unsubscribe = null;
  let closed = false;
  // Set while the handshake is in flight; once null, notifications are status.
  let handshake = null;

  const close = async () => {
    if (closed) return;
    closed = true;
    if (unsubscribe) await unsubscribe();
    await disconnect(deviceId);
  };

  try {
    trace('info', `connecting to ${uniqueNumber}`);
    await connect(deviceId, () => {
      closed = true;
      trace('info', 'link dropped');
      if (handshake) handshake.fail('LINK_LOST', 'The device disconnected.');
      if (onDisconnect) onDisconnect();
    });

    // Subscribe BEFORE writing anything. The device replies within
    // milliseconds; a listener attached afterwards misses the NONCE and the
    // handshake times out on a device that answered perfectly.
    unsubscribe = await listen(deviceId, (text) => {
      trace('rx', text);
      if (handshake) { handshake.onText(text); return; }
      if (onStatus) onStatus(text);
    });

    const result = await runHandshake({
      deviceId, uniqueNumber, ownerSecret, keyVersion, trace,
      setHandshake: (h) => { handshake = h; },
    });
    handshake = null;
    trace('info', result.ok ? 'authenticated' : `handshake failed: ${result.code}`);

    if (!result.ok) {
      await close();
      return result;
    }

    return {
      ok: true,
      session: {
        deviceId,
        uniqueNumber,
        assigned: result.assigned,
        // Traced too, so a bring-up log shows commands alongside the handshake
        // rather than going quiet the moment auth succeeds.
        send: (command) => { trace('tx', command); return write(deviceId, command); },
        close,
        isClosed: () => closed,
      },
    };
  } catch (e) {
    await close();
    return { ok: false, code: 'BLE_ERROR', error: (e && e.message) || 'Bluetooth error' };
  }
}

// The AUTH exchange. Split out so the notification routing above stays readable.
function runHandshake({
  deviceId, uniqueNumber, ownerSecret, keyVersion, setHandshake, trace,
}) {
  // Every write is traced, so a bring-up log shows the exact bytes that went out
  // next to what came back — which is the only way to tell a key mismatch apart
  // from a truncated write.
  const send = (text) => {
    trace('tx', text);
    return write(deviceId, text);
  };
  return new Promise((resolve) => {
    let settled = false;
    let assigned = false;
    // Stage 2 must answer the SAME nonce as stage 1, so it is held here for the
    // life of this handshake — and only this one.
    let nonceHex = '';

    const finish = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      setHandshake(null);
      resolve(value);
    };

    const timer = setTimeout(() => finish({
      ok: false,
      code: 'TIMEOUT',
      error: 'The device did not answer. Move closer and try again.',
    }), HANDSHAKE_TIMEOUT_MS);

    setHandshake({
      fail: (code, error) => finish({ ok: false, code, error }),
      onText: (text) => {
        const nonce = parseNonce(text);
        if (nonce) {
          assigned = nonce.assigned;
          // Stage 1 — always answered with the derived device key.
          send(buildAuthAnswer(
            authResponse(uniqueNumber, nonce.nonceHex, keyVersion),
          )).catch((e) => finish({
            ok: false, code: 'BLE_ERROR', error: (e && e.message) || 'Write failed',
          }));
          nonceHex = nonce.nonceHex;
          return;
        }

        if (isOwnerRequired(text)) {
          if (!ownerSecret) {
            // Recoverable, and the fix is specific: fetch this device's secret
            // from the backend. Saying "auth failed" would send the user
            // looking at the wrong thing entirely.
            finish({
              ok: false,
              code: 'OWNER_SECRET_MISSING',
              error: 'This device is claimed and its owner key is not on this '
                + 'phone. Sign in while online to fetch it.',
            });
            return;
          }
          send(buildOwnerAnswer(
            ownerResponse(ownerSecret, nonceHex),
          )).catch((e) => finish({
            ok: false, code: 'BLE_ERROR', error: (e && e.message) || 'Write failed',
          }));
          return;
        }

        if (isAuthOk(text)) { finish({ ok: true, assigned }); return; }

        if (isAuthFail(text)) {
          // NOT retried. Three failures and the device drops the link, so a
          // retry loop against a wrong key locks it out — the spec is explicit.
          finish({
            ok: false,
            code: 'AUTH_FAIL',
            error: 'The device rejected this app’s key.',
          });
        }
      },
    });

    // Kick it off.
    send(CMD_AUTH).catch((e) => finish({
      ok: false, code: 'BLE_ERROR', error: (e && e.message) || 'Write failed',
    }));
  });
}
